/**
 * This uses a combination of Portal, Dimmer and Sidebar to slide out a drawer from the right edge.
 * Implemented in a manner by which it can be called similar to a modal, not requiring any
 * over-arching markup - just what is need to render the drawer.
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Portal, Segment, Sidebar, Dimmer } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const baseElmId = 'drawer'

const SIDEBAR_SLIDE_TIME_MS = 500
export const DIRECTIONS = {
  BOTTOM: 'bottom',
  RIGHT: 'right',
}
export const HEADER_STYLES = {
  LIGHT: 'light',
  INVERTED: 'inverted',
  CLEAR: 'clear',
}

class Drawer extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    displayPartially: PropTypes.bool,
    title: PropTypes.node.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    drawerRef: PropTypes.func,
    footerLeftButton: PropTypes.node, // DEPRECATED - use footerPrimaryButton
    footerRightButton: PropTypes.node, // DEPRECATED - use footerSecondaryButton
    footerPrimaryButton: PropTypes.node,
    footerSecondaryButton: PropTypes.node,
    noScroll: PropTypes.bool,
    progressBar: PropTypes.node,
    isWide: PropTypes.bool,
    direction: PropTypes.string,
    headerStyle: PropTypes.oneOf(Object.values(HEADER_STYLES)),
    closeOnDimmerClick: PropTypes.bool,
    elmId: PropTypes.string,
    infiniteScroll: PropTypes.bool,
  }

  static defaultProps = {
    displayPartially: false,
    drawerRef: _.noop,
    isWide: false,
    direction: DIRECTIONS.RIGHT,
    headerStyle: HEADER_STYLES.LIGHT,
    closeOnDimmerClick: true,
    infiniteScroll: false,
    noScroll: false,
  }

  state = {
    sliderOpen: false,
    portalOpen: false,
    transitioning: false,
    hovering: false,
  }

  /**
   * Here we close up the portal after the slider is closed. However we have to wait so
   * that we don't unmount sidebar before it's done with animation.
   */
  transitionCloseEnd = () => {
    this.drawerRef.removeEventListener('transitionend', this.transitionCloseEnd)
    setTimeout(() => {
      this.setState({ portalOpen: false, transitioning: false })
    }, SIDEBAR_SLIDE_TIME_MS)
  }

  /**
   * Handle portal and slider open/close based on open prop.  I bit of a timing thing here
   * as we have to account for transitioning...
   */
  togglePortalAndSlider = prevProps => {
    const { transitioning } = this.state
    if (transitioning) return

    if (!prevProps.open && this.props.open) {
      // open portal but let render run before we open slider
      this.setState({
        portalOpen: true,
        sliderOpen: false,
        transitioning: true,
      })
      _.defer(() => {
        if (this.state.transitioning)
          this.setState({ sliderOpen: true, transitioning: false })
      })
    } else if (prevProps.open && !this.props.open) {
      // close slider
      this.drawerRef.addEventListener('transitionend', this.transitionCloseEnd)
      this.setState({
        portalOpen: true,
        sliderOpen: false,
        transitioning: true,
      })
    }
  }

  componentDidMount() {
    // call this on mount in case portal is being mounted and opened simultaneously
    this.togglePortalAndSlider({})
  }

  componentDidUpdate(prevProps) {
    this.togglePortalAndSlider(prevProps)
  }

  captureDrawerRef = ref => {
    const { drawerRef } = this.props
    this.drawerRef = ref && ref.parentElement

    // send drawer ref to consumer (mainly needed for unit testing)
    if (drawerRef) {
      drawerRef(this.drawerRef)
    }
  }

  render() {
    const {
      children,
      displayPartially,
      title,
      onClose,
      footerLeftButton,
      footerRightButton,
      footerPrimaryButton: _footerPrimaryButton,
      footerSecondaryButton: _footerSecondaryButton,
      progressBar,
      noScroll,
      isWide,
      direction,
      headerStyle,
      closeOnDimmerClick,
      elmId = baseElmId,
      infiniteScroll,
    } = this.props
    const { portalOpen, sliderOpen } = this.state
    const footerPrimaryButton = _footerPrimaryButton || footerLeftButton
    const footerSecondaryButton = _footerSecondaryButton || footerRightButton
    const showFooter = Boolean(footerPrimaryButton || footerSecondaryButton)

    return (
      <Portal open={portalOpen}>
        <Sidebar.Pushable
          as={Segment}
          basic
          className={cx(
            'container',
            displayPartially && 'partial-container',
            direction === DIRECTIONS.RIGHT && isWide && 'wide',
            direction === DIRECTIONS.BOTTOM && 'bottom'
          )}
        >
          <Sidebar
            as='div'
            animation='overlay'
            width='very wide'
            direction={direction}
            visible={sliderOpen}
            icon='labeled'
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            data-elm-id={elmId}
          >
            <div
              ref={this.captureDrawerRef}
              className={cx('panel', { 'no-scroll': noScroll })}
            >
              <div
                className={cx('header', headerStyle)}
                data-elm-id={`${elmId}_header`}
              >
                <div className={cx('title')} data-elm-id={`${elmId}_title`}>
                  {title}
                </div>
                <div
                  className={cx('close-button', headerStyle)}
                  onClick={onClose}
                  data-elm-id={`${elmId}_close_button`}
                >
                  <Icon
                    name='times'
                    iconStyle='light'
                    color={
                      headerStyle === HEADER_STYLES.INVERTED
                        ? 'grayExtraLight'
                        : 'gray'
                    }
                    hoverColor={
                      headerStyle === HEADER_STYLES.INVERTED
                        ? 'offWhite'
                        : 'grayDark'
                    }
                  />
                </div>
              </div>
              <div
                className={cx(
                  'content',
                  infiniteScroll && 'scrolling',
                  noScroll && 'no-scroll'
                )}
                data-elm-id={`${elmId}_content`}
              >
                {children}
              </div>
              {showFooter && (
                <div className={cx('footer')} data-elm-id={`${elmId}_footer`}>
                  {progressBar}
                  {footerSecondaryButton}
                  {footerPrimaryButton}
                </div>
              )}
            </div>
          </Sidebar>
          <Dimmer
            active
            page
            className={cx('dimmer', { visible: sliderOpen })}
            onClick={closeOnDimmerClick ? onClose : _.noop}
          />
        </Sidebar.Pushable>
      </Portal>
    )
  }
}

export default Drawer
